import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  auth,
  signInWithGoogle,
  signInWithEmail,
  registerWithEmail,
  resetPassword,
  signOut as firebaseSignOut,
} from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { analytics } from '../firebase';
import {
  setUserId,
  setUserProperties,
  logEvent as gaLogEvent,
} from 'firebase/analytics';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      try {
        if (analytics) {
          if (user) {
            setUserId(analytics, user.uid);
            // Optionally set basic properties; avoid PII
            setUserProperties(analytics, {
              auth_provider: user.providerData[0]?.providerId || 'unknown',
            });
            gaLogEvent(analytics, 'auth_login');
          } else {
            setUserId(analytics, null);
            gaLogEvent(analytics, 'auth_logout');
          }
        }
      } catch (_) {}
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (method = 'google', credentials = null) => {
    try {
      let result;
      if (method === 'email' && credentials) {
        result = await signInWithEmail(credentials.email, credentials.password);
      } else {
        result = await signInWithGoogle();
      }
      return result;
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const register = async (email, password) => {
    try {
      const result = await registerWithEmail(email, password);
      return result;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const resetPasswordEmail = async (email) => {
    try {
      await resetPassword(email);
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      // Clear selected players data from localStorage
      localStorage.removeItem('selectedPlayerIds');

      await firebaseSignOut();
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const value = {
    user,
    signIn,
    register,
    resetPasswordEmail,
    signOut,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
